import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Pill,
  SectionHead,
  StatusBar,
  StatusDot,
  TableWrap,
  Td,
  Th,
  Tr,
} from '../components/DataTable';
import { PageHeader } from '../components/PageHeader';
import { StatTile } from '../components/StatTile';
import { useLeads, useTools } from '../lib/queries';

function leadStatusDot(status: string): 'live' | 'paused' | 'draft' | 'neutral' {
  if (status === 'sold' || status === 'paid' || status === 'converted' || status === 'delivered') {
    return 'live';
  }
  if (status === 'qualified' || status === 'listed_for_sale') return 'paused';
  if (status === 'rejected') return 'draft';
  return 'neutral';
}

export function LeadsList() {
  const tools = useTools();
  const [toolId, setToolId] = useState<string | undefined>();
  const leads = useLeads(toolId);

  const list = leads.data ?? [];

  const stats = useMemo(() => {
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const last7 = list.filter((l) => new Date(l.created_at).getTime() >= sevenDaysAgo).length;
    const sold = list.filter((l) => l.status === 'sold').length;
    const captured = list.filter((l) => l.status === 'captured').length;
    return { total: list.length, last7, sold, captured };
  }, [list]);

  return (
    <div>
      <PageHeader
        breadcrumb="Tool Factory · Leads"
        title="Leads"
        subtitle="Captured leads across your tools. Click a row to manage status and route to sale."
        actions={
          <select
            className="rounded-md border border-border bg-card px-3 py-[8px] font-heading text-[12px] font-medium text-foreground transition-colors hover:bg-secondary/60"
            value={toolId ?? ''}
            onChange={(e) => setToolId(e.target.value || undefined)}
          >
            <option value="">All tools</option>
            {tools.data?.map((t) => (
              <option key={t.id} value={t.id}>
                {t.slug}
              </option>
            ))}
          </select>
        }
      />

      <div className="mb-8 grid grid-cols-1 gap-[14px] md:grid-cols-2 lg:grid-cols-4">
        <StatTile label="Total leads" value={stats.total} delta="All-time" />
        <StatTile label="Leads · 7d" value={stats.last7} delta="Last 7 days" />
        <StatTile label="Captured" value={stats.captured} delta="Awaiting qualification" />
        <StatTile
          label="Sold"
          accent="gold"
          value={stats.sold}
          delta={stats.sold === 0 ? 'No sales yet' : 'Converted'}
        />
      </div>

      <SectionHead title={toolId ? 'Filtered leads' : 'All leads'} meta="Live data" />

      <TableWrap>
        <thead>
          <tr>
            <Th>Created</Th>
            <Th>Status</Th>
            <Th>Email</Th>
            <Th>Phone</Th>
            <Th align="right">Score</Th>
            <Th>Tool</Th>
          </tr>
        </thead>
        <tbody>
          {list.length === 0 ? (
            <Tr hoverable={false}>
              <Td>
                <span className="text-foreground/50">
                  {toolId ? 'No leads for this tool.' : 'No leads captured yet.'}
                </span>
              </Td>
              <Td>
                <span className="text-foreground/30">—</span>
              </Td>
              <Td>
                <span className="text-foreground/30">—</span>
              </Td>
              <Td>
                <span className="text-foreground/30">—</span>
              </Td>
              <Td align="right">
                <span className="text-foreground/30">—</span>
              </Td>
              <Td>
                <span className="text-foreground/30">—</span>
              </Td>
            </Tr>
          ) : (
            list.map((l) => {
              const toolSlug = tools.data?.find((t) => t.id === l.tool_id)?.slug;
              return (
                <Tr key={l.id}>
                  <Td>
                    <Link
                      to={`/leads/${l.id}`}
                      className="font-mono text-[12px] text-foreground/85 hover:text-gold transition-colors"
                    >
                      {new Date(l.created_at).toLocaleString()}
                    </Link>
                  </Td>
                  <Td>
                    <span className="inline-flex items-center gap-[6px] text-[12px]">
                      <StatusDot kind={leadStatusDot(l.status)} />
                      {l.status}
                    </span>
                  </Td>
                  <Td>
                    {l.email ? (
                      <span className="text-[13px]">{l.email}</span>
                    ) : (
                      <span className="text-foreground/30">—</span>
                    )}
                  </Td>
                  <Td>
                    {l.phone ? (
                      <span className="font-mono text-[12px]">{l.phone}</span>
                    ) : (
                      <span className="text-foreground/30">—</span>
                    )}
                  </Td>
                  <Td align="right">
                    {l.score != null ? (
                      <span className="font-mono text-[13px] font-semibold">{l.score}</span>
                    ) : (
                      <span className="text-foreground/30">—</span>
                    )}
                  </Td>
                  <Td>
                    {toolSlug ? (
                      <Pill>{toolSlug}</Pill>
                    ) : (
                      <span className="font-mono text-[11px] text-foreground/40">
                        {l.tool_id.slice(0, 8)}
                      </span>
                    )}
                  </Td>
                </Tr>
              );
            })
          )}
        </tbody>
      </TableWrap>

      <StatusBar
        left={`${list.length} leads${toolId ? ' (filtered)' : ''}`}
        right="tools.holdengr.com"
      />
    </div>
  );
}
